import React from 'react';
import { responsiveFontSize, responsiveIconSize } from '../../util/responsiveUtils';
import { Typography, Box, useTheme } from '@mui/material';
import { Update as UpdateIcon } from '@mui/icons-material';
import { VersionCheckResult } from '../../services/appVersionService';
import { AppVersionService } from '../../services/appVersionService';
import { Z_INDEX } from '../../constants/zIndex';
import { getButtonColor, getButtonHoverColor } from '../../theme/themeUtils';
import { CustomButton } from './CustomButton';
import { AppDialog } from './AppDialog';

interface ForceUpdateModalProps {
  open: boolean;
  versionInfo: VersionCheckResult | null;
}

/**
 * ForceUpdateModal Component
 *
 * A blocking modal that appears when the app version is below the minimum required version.
 * Users cannot dismiss this modal and must update the app to continue.
 */
export const ForceUpdateModal: React.FC<ForceUpdateModalProps> = ({
  open,
  versionInfo,
}) => {
  const theme = useTheme();
  const appVersionService = AppVersionService.getInstance();

  const handleUpdate = async () => {
    if (versionInfo?.storeUrl) {
      await appVersionService.openAppStore(versionInfo.storeUrl);
    }
  };

  if (!versionInfo) {
    return null;
  }

  const titleNode = (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
      <Box
        sx={{
          width: { xs: 56, sm: 64 },
          height: { xs: 56, sm: 64 },
          borderRadius: '50%',
          backgroundColor: getButtonColor(theme),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#ffffff',
          boxShadow: `0 4px 12px ${getButtonColor(theme)}40`,
        }}
      >
        <UpdateIcon sx={{ fontSize: responsiveIconSize.icon32_36, color: '#ffffff' }} />
      </Box>
      <Typography variant="h6" component="div" fontWeight={700} sx={{ color: theme.palette.text.primary, fontSize: responsiveFontSize.headingMd }}>
        Update Required
      </Typography>
    </Box>
  );

  return (
    <AppDialog
      open={open}
      onClose={() => {}}
      title={titleNode}
      showCloseButton={false}
      maxWidth="sm"
      fullWidth
      maxHeightDesktop="90vh"
      zIndex={Z_INDEX.FORCE_UPDATE_MODAL}
      disableBackdropClick
      disableEscapeKeyDown
      divider={false}
      titleSx={{ textAlign: 'center', pt: { xs: 3, sm: 4 }, pb: { xs: 2, sm: 2 } }}
      contentSx={{ textAlign: 'center' }}
      actionsSx={{ justifyContent: 'center', pb: { xs: 2, sm: 3 } }}
      actions={
        <CustomButton
          variant="contained"
          size="medium"
          onClick={handleUpdate}
          fullWidth
          sx={{
            py: { xs: 1.75, sm: 1.5 },
            fontSize: responsiveFontSize.bodyMedium,
            borderRadius: 2,
            minHeight: { xs: '48px', sm: 'auto' },
            boxShadow: `0 4px 12px ${getButtonColor(theme)}40`,
            '@media (hover: hover)': {
              '&:hover': {
                boxShadow: `0 6px 16px ${getButtonHoverColor(theme)}50`,
              },
            },
            '&:active': {
              boxShadow: `0 2px 8px ${getButtonColor(theme)}30`,
            },
          }}
        >
          Update Now
        </CustomButton>
      }
      BackdropProps={{
        sx: {
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.85)' : 'rgba(0, 0, 0, 0.7)',
          zIndex: Z_INDEX.FORCE_UPDATE_MODAL_BACKDROP,
        },
      }}
      paperSx={{ position: 'relative' as const, zIndex: Z_INDEX.FORCE_UPDATE_MODAL_CONTENT }}
    >
      <Typography
        variant="body1"
        sx={{
          mb: { xs: 3, sm: 3 },
          color: theme.palette.text.secondary,
          lineHeight: 1.6,
          fontSize: responsiveFontSize.bodyMedium,
        }}
      >
        A new version of Virtoly is available. Please update to continue using the app.
      </Typography>
    </AppDialog>
  );
};
